import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { catchError, distinctUntilChanged, of, switchMap, take, timer } from 'rxjs';
import config from '../../../config';
import { ProxyEndPoints } from '../config/endpoints';
import {
  UnacknowledgedJob,
  UnacknowledgedNotifications,
} from '../models/notification.model';
import { AuthService } from '../../core/auth/services/auth.service';
import { VendorOrdersService } from '../../core/layout/pages/translation-manager/services/vendor-orders.service';
import { VendorOrder } from '../models/vendor-order.model';

const POLLING_INTERVAL_MS = 30_000;
const IGNORED_AWAITING_VENDOR_STATUS = 'ON_HOLD';
const IGNORED_COMPLETED_DOCS_STATUS = 'FAILED';
const AWAITING_VENDOR_APPROVAL_STATUS = 'AWAITING_VENDOR_APPROVAL';
const AWAITING_VENDOR_SELECTION_STATUS = 'AWAITING_VENDOR_SELECTION';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly vendorOrdersService = inject(VendorOrdersService);

  private readonly notifications = signal<UnacknowledgedNotifications>({
    awaitingVendor: [],
    completedDocs: [],
  });

  /** Jobs with AWAITING_VENDOR_SELECTION or AWAITING_VENDOR_APPROVAL status. */
  readonly awaitingVendorJobs = computed<UnacknowledgedJob[]>(() =>
    this.notifications().awaitingVendor.filter(
      (j) =>
        j.jobStatus === 'AWAITING_VENDOR_SELECTION' ||
        j.jobStatus === 'AWAITING_VENDOR_APPROVAL',
    ),
  );

  /** Jobs with COMPLETED status from completedDocs. */
  readonly completedJobs = computed<UnacknowledgedJob[]>(() =>
    this.notifications().completedDocs.filter(
      (j) => j.jobStatus === 'COMPLETED',
    ),
  );

  /** Ignored vendor notifications that should be silently cleaned up. */
  readonly ignoredAwaitingVendorJobs = computed<UnacknowledgedJob[]>(() =>
    this.notifications().awaitingVendor.filter(
      (j) => j.jobStatus === IGNORED_AWAITING_VENDOR_STATUS,
    ),
  );

  /** Ignored completed-doc notifications that should be silently cleaned up. */
  readonly ignoredCompletedDocsJobs = computed<UnacknowledgedJob[]>(() =>
    this.notifications().completedDocs.filter(
      (j) => j.jobStatus === IGNORED_COMPLETED_DOCS_STATUS,
    ),
  );

  /** Whether the "My Translation Jobs" card should show a dot (any COMPLETED doc). */
  readonly hasCompletedDocsNotification = computed(
    () => this.completedJobs().length > 0,
  );

  /** Whether the "Quotes to be Reviewed" card should show a dot (any awaitingVendor job). */
  readonly hasAwaitingVendorNotification = computed(
    () => this.awaitingVendorJobs().length > 0,
  );

  /** Check if a specific job UID is in the awaitingVendor list. */
  isAwaitingVendorJob(jobUid: string): boolean {
    return this.awaitingVendorJobs().some((j) => j.jobUid === jobUid);
  }

  /** Check if a specific job UID is in the completedDocs list. */
  isCompletedDocsJob(jobUid: string): boolean {
    return this.completedJobs().some((j) => j.jobUid === jobUid);
  }

  /** Returns the set of notification status labels that should show a dot in the sidebar. */
  readonly notifiedStatusLabels = computed<Set<string>>(() => {
    const labels = new Set<string>();

    for (const job of this.awaitingVendorJobs()) {
      if (job.jobStatus === 'AWAITING_VENDOR_SELECTION') {
        labels.add('Final Quote to be Reviewed');
      }
      if (job.jobStatus === 'AWAITING_VENDOR_APPROVAL') {
        labels.add('Final Quote Pending');
      }
    }

    for (const job of this.completedJobs()) {
      if (job.jobStatus === 'COMPLETED') {
        labels.add('Completed');
      }
    }

    return labels;
  });

  /**
   * Stable fingerprint of current unacknowledged notifications.
   * Used to detect whether notification data changed while navigating.
   */
  readonly notificationSnapshotKey = computed<string>(() => {
    const jobs = [...this.awaitingVendorJobs(), ...this.completedJobs()]
      .map((job) => `${job.jobUid}:${job.jobStatus}`)
      .sort();

    return jobs.join('|');
  });

  /** Silently acknowledge ignored notifications (ON_HOLD/FAILED). */
  cleanupIgnoredNotifications(): void {
    for (const job of this.ignoredAwaitingVendorJobs()) {
      this.acknowledgeVendorJob(job.jobUid);
    }

    for (const job of this.ignoredCompletedDocsJobs()) {
      this.acknowledgeCompletedJob(job.jobUid);
    }
  }

  /** Check whether a valid (non-expired) auth token exists. */
  private hasValidToken(): boolean {
    const auth = this.authService.getAuthFromLocalStorage();
    if (!auth?.payload?.exp) {
      return false;
    }
    const now = Math.floor(Date.now() / 1000);
    return auth.payload.exp > now;
  }

  /** Start polling. Call once from AppComponent. */
  startPolling(): void {
    const url = `${config.API_ENDPOINT}${ProxyEndPoints.unacknowledgedCount}`;
    const empty: UnacknowledgedNotifications = { awaitingVendor: [], completedDocs: [] };

    this.authService.currentUser$
      .pipe(
        distinctUntilChanged(),
        switchMap((user) => {
          if (!user) {
            this.notifications.set(empty);
            return of<UnacknowledgedNotifications>();
          }
          return timer(0, POLLING_INTERVAL_MS).pipe(
            switchMap(() => {
              if (!this.hasValidToken()) {
                return of(empty);
              }
              return this.http.get<UnacknowledgedNotifications>(url).pipe(
                catchError(() => of(empty)),
              );
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response) {
          const awaitingVendor = response.awaitingVendor ?? [];
          const pendingApprovalNotifications = awaitingVendor.filter(
            (job) => job.jobStatus === AWAITING_VENDOR_APPROVAL_STATUS,
          );

          this.notifications.set({
            // Hold AWAITING_VENDOR_APPROVAL notifications until they are validated.
            awaitingVendor: awaitingVendor.filter(
              (job) => job.jobStatus !== AWAITING_VENDOR_APPROVAL_STATUS,
            ),
            completedDocs: response.completedDocs ?? [],
          });
          this.reconcileAwaitingVendorApprovalNotifications(
            pendingApprovalNotifications,
          );
          this.cleanupIgnoredNotifications();
        }
      });
  }

  private reconcileAwaitingVendorApprovalNotifications(
    pendingApprovalNotifications: UnacknowledgedJob[],
  ): void {
    if (pendingApprovalNotifications.length === 0) {
      return;
    }

    this.vendorOrdersService
      .getVendorOrders(false, true)
      .pipe(
        take(1),
        catchError(() => of([] as VendorOrder[])),
      )
      .subscribe((orders) => {
        const orderStatusesByUid = new Map<string, string>();

        for (const order of orders ?? []) {
          if (!order.jobUid || !order.jobStatus) {
            continue;
          }
          orderStatusesByUid.set(order.jobUid, order.jobStatus);
        }

        const jobsToAcknowledge: string[] = [];

        const validatedSelectionByUid = new Map<string, UnacknowledgedJob>();

        for (const notificationJob of pendingApprovalNotifications) {
          const currentOrderStatus = orderStatusesByUid.get(notificationJob.jobUid);
          if (currentOrderStatus === AWAITING_VENDOR_SELECTION_STATUS) {
            validatedSelectionByUid.set(notificationJob.jobUid, {
              ...notificationJob,
              jobStatus: AWAITING_VENDOR_SELECTION_STATUS,
            });
            continue;
          }

          jobsToAcknowledge.push(notificationJob.jobUid);
        }

        this.notifications.update((current) => {
          const existingSelectionUids = new Set(
            current.awaitingVendor.map((job) => job.jobUid),
          );
          const newSelections = Array.from(validatedSelectionByUid.values()).filter(
            (job) => !existingSelectionUids.has(job.jobUid),
          );

          return {
            ...current,
            awaitingVendor: [...current.awaitingVendor, ...newSelections],
          };
        });

        for (const jobUid of jobsToAcknowledge) {
          this.acknowledgeVendorJob(jobUid);
        }
      });
  }

  /** Acknowledge a vendor-related notification (Quotes tab opened). */
  acknowledgeVendorJob(jobUid: string): void {
    const url = `${config.API_ENDPOINT}${ProxyEndPoints.unacknowledgedRefresh}`;
    this.http.post(url, { isVendorJob: true, jobUid }).pipe(catchError(() => of(null))).subscribe(() => {
      this.notifications.update((current) => ({
        ...current,
        awaitingVendor: current.awaitingVendor.filter(
          (j) => j.jobUid !== jobUid,
        ),
      }));
    });
  }

  /** Acknowledge a completed-docs notification (Translated Files tab opened). */
  acknowledgeCompletedJob(jobUid: string): void {
    const url = `${config.API_ENDPOINT}${ProxyEndPoints.unacknowledgedRefresh}`;
    this.http.post(url, { isVendorJob: false, jobUid }).pipe(catchError(() => of(null))).subscribe(() => {
      this.notifications.update((current) => ({
        ...current,
        completedDocs: current.completedDocs.filter(
          (j) => j.jobUid !== jobUid,
        ),
      }));
    });
  }
}
